import { OwnershipLabelsProvider } from "../kyc/OwnershipLabels";
import { normalizeKycNames } from "../utils/individualName";
import useNameEntryMode from "../utils/useNameEntryMode";
import { evaluateKycCompletion } from "../utils/kycCompletion";
import { sanctionsSourcesForJurisdiction } from "../utils/sanctionsNameMatch";
// KycFormPage.jsx

import { useEffect, useLayoutEffect, useMemo, useState, useRef, useCallback } from "react";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { Toast } from "primereact/toast";
import useKycDocumentDrop from "../kyc/hooks/useKycDocumentDrop";
import { applyDocumentTargetValues } from "../kyc/kycDocumentTargets";
import useKycDocumentIntake from "../kyc/hooks/useKycDocumentIntake";
import KycDocumentIntakeModal from "../kyc/KycDocumentIntakeModal";
import KycDocumentProcessingOverlay from "../kyc/KycDocumentProcessingOverlay";

// ✅ Shared external KYC sections (same as client/review)
import KycSectionCustomerDetails from "../kyc/sections/common/KycSectionCustomerDetails";
import KycClientOwnershipDesktop from "../kyc/sections/client/KycClientOwnershipDesktop";
import KycClientSignatoriesDesktop from "../kyc/sections/client/KycClientSignatoriesDesktop";
import {
  buildLiveReviewOwnershipSnapshot,
  buildReviewOwnershipApiPayload,
} from "../kyc/sections/review/KycReviewOwnershipSection";
import KycSectionSowSof from "../kyc/sections/common/KycSectionSowSof";
import KycSectionPepSanctions from "../kyc/sections/common/KycSectionPepSanctions";
import KycSectionRiskAssessment from "../kyc/sections/common/KycSectionRiskAssessment";
import KycSectionSanctionsWithScreening from "../kyc/sections/common/KycSectionSanctionsWithScreening";
import KycSectionPepWithScreening from "../kyc/sections/common/KycSectionPepWithScreening";
// Staff front sees the screening sections exactly as /kyc-view renders them.
import KycViewPep from "../kyc/sections/view/KycViewPep";
import KycViewSanctions from "../kyc/sections/view/KycViewSanctions";
import KycViewAdverseMedia from "../kyc/sections/view/KycViewAdverseMedia";
import KycViewRiskAssessment from "../kyc/sections/view/KycViewRiskAssessment";
import KycSectionDocuments from "../kyc/sections/common/KycSectionDocuments";
import KycSectionAdverseMediaWithScreening from "../kyc/sections/common/KycSectionAdverseMediaWithScreening";
import KycSectionFinalOverview from "../kyc/sections/common/KycSectionFinalOverview";

// ✅ Top Nav (fixed) for scrolling across sections
import AdverseMediaTopNav from "../components/ui/AdverseMediaTopNav";

// Sync mutual data client data and kyc data
import {
  clientToKycCustomer,
  kycCustomerToClientFields,
} from "../utils/syncClientKyc";
import { MAIL_API_BASE } from "../config/api";
import {
  normalizeClientKycData,
  removeUndefinedDeep,
} from "../utils/clientKycPayload";
import {
  assessUbosFromTree,
  applyUboAssessmentResultToOwnership,
} from "../utils/uboAssessment";
import { ownershipAssessmentEditSignature, storedUboAssessmentIsCurrent, useLiveUboAssessment } from "../kyc/hooks/useLiveUboAssessment";
import {
  DEFAULT_RELATIONSHIP_RISK_SETTINGS,
  RELATIONSHIP_RISK_DEFAULT_DOC_ID,
  RELATIONSHIP_RISK_SETTINGS_COLLECTION,
  sanitizeRelationshipRiskSettings,
} from "../utils/relationshipRiskRules";

// IMPORT LOGOUT firebase
import { signOut } from "firebase/auth";
import { auth } from "../firebase";
import { normalizeRole, isReadOnlyArea, PROTECTED_AREAS, canPersistScreening } from "../auth/permissions";
import { maskKycPayload as maskKycPayloadPure } from "../utils/kycSaveMask";
import KycReadOnlyBanner from "../kyc/sections/common/KycReadOnlyBanner";

import {
  doc,
  getDoc,
  setDoc,
  updateDoc,
  serverTimestamp,
  onSnapshot,
  collection,
  query,
  where,
  orderBy,
  limit,
} from "firebase/firestore";
import { db } from "../firebase";

import { useAppLoading } from "../context/AppLoadingContext";
import iconHomeA from "../images/home-A.svg";
import doubleTapFullIcon from "../images/double-tap-full.svg";
import { useStaffTopBand } from "../components/ui/StaffTopBandContext";
import "./css/KycFormPage.css";

// Animation setting

const pageAnim = {
  initial: { opacity: 0, y: 10 },
  animate: { opacity: 1, y: 0 },
  exit: { opacity: 0, y: -10 },
};

const CLIENT_REQUEST_OPTIONS = [
  "Entity setup",
  "Bank account assistance",
  "Ongoing bookkeeping",
  "Tax/VAT support",
  "Corporate amendments",
  "Other",
];

const APPROACH_CHANNEL_OPTIONS = ["Direct", "Referral", "Introducer"];

const TX_CHANNEL_OPTIONS = [
  "UAE transfers",
  "International transfers",
  "Cash",
  "Cheques",
  "Crypto / virtual assets",
];

function emptyRow(keys) {
  const o = {};
  keys.forEach((k) => (o[k] = ""));
  return o;
}

function stableStringify(value) {
  const seen = new WeakSet();
  return JSON.stringify(value, (key, val) => {
    if (val && typeof val === "object") {
      if (seen.has(val)) return undefined;
      seen.add(val);

      if (val instanceof Date) return val.toISOString();
      if (typeof val.toDate === "function") return val.toDate().toISOString();

      if (!Array.isArray(val)) {
        return Object.keys(val)
          .sort()
          .reduce((acc, k) => {
            acc[k] = val[k];
            return acc;
          }, {});
      }
    }
    return val;
  });
}

// HELPER FUNCTIONS:
function normalizeClientType(raw) {
  const v = String(raw || "")
    .trim()
    .toLowerCase();

  // support both new values + old values
  if (v === "entity" || v === "company") return "entity";
  if (v === "individual" || v === "person") return "individual";

  return ""; // unknown / empty
}

function typeLabel(v) {
  if (v === "entity") return "Entity";
  if (v === "individual") return "Individual";
  return "";
}

// Helper date selection
function toDateOrNull(v) {
  if (!v) return null;
  if (v instanceof Date) return v;
  if (typeof v?.toDate === "function") return v.toDate(); // Firestore Timestamp
  const d = new Date(v);
  return Number.isNaN(d.getTime()) ? null : d;
}

// Helper
function joinUrl(base, path) {
  return (
    String(base || "").replace(/\/+$/, "") +
    "/" +
    String(path || "").replace(/^\/+/, "")
  );
}

const SERVER_URL = MAIL_API_BASE;

const ORG_GEO_PREFERRED_COUNTRY = {
  uae: "AE",
  switzerland: "CH",
};

function orgGeoToPreferredCountry(value) {
  return ORG_GEO_PREFERRED_COUNTRY[String(value || "").trim().toLowerCase()] || "";
}

function countryRiskUsesMainCountryOfOperation(orgData, defaultData) {
  if (typeof orgData?.useMainCountryOfOperation === "boolean") {
    return orgData.useMainCountryOfOperation;
  }
  return defaultData?.useMainCountryOfOperation === true;
}

function effectiveRelationshipRiskSettings(orgData, defaultData) {
  const defaultSettings = sanitizeRelationshipRiskSettings(defaultData?.rules || defaultData);
  if (!orgData || orgData.useGeneralSettings === true) return defaultSettings;
  const orgSettings = sanitizeRelationshipRiskSettings(orgData.rules || orgData);
  return orgData.useGeneralModel === true
    ? {
        ...orgSettings,
        activeModel: defaultSettings.activeModel,
      }
    : orgSettings;
}

const DEFAULT_SANCTIONS_SCREENING_SOURCES = ["ofac", "un"];



// -------------------------------------------------------
// -----------------   EXPORT FUNCTION   -----------------
// -------------------------------------------------------

export default function KycFormPage() {
  const { id } = useParams(); // client id
  const nav = useNavigate();
  const location = useLocation();
  const { showOverlay, hideOverlay } = useAppLoading();
  const [isAdmin, setIsAdmin] = useState(false);
  const [myOrgId, setMyOrgId] = useState("");
  // Fail closed until the profile loads (deny-by-default, see auth/permissions).
  const [myRole, setMyRole] = useState("");
  const [myTeamId, setMyTeamId] = useState("");
  const [meLoading, setMeLoading] = useState(true);
  const [clientOrgId, setClientOrgId] = useState("");
  const [clientAccessScope, setClientAccessScope] = useState("");
  const [clientOwnerUid, setClientOwnerUid] = useState("");
  const [clientTeamId, setClientTeamId] = useState("");
  const [orgPreferredPhoneCountry, setOrgPreferredPhoneCountry] = useState("");
  const [orgJurisdiction, setOrgJurisdiction] = useState(null);
  const [useMainCountryOfOperation, setUseMainCountryOfOperation] = useState(false);
  const [relationshipRiskSettings, setRelationshipRiskSettings] = useState(() =>
    sanitizeRelationshipRiskSettings(DEFAULT_RELATIONSHIP_RISK_SETTINGS)
  );
  const [sanctionsScreeningSources, setSanctionsScreeningSources] = useState(
    DEFAULT_SANCTIONS_SCREENING_SOURCES
  );

  const [loading, setLoading] = useState(true);
  const [savedDraftSignature, setSavedDraftSignature] = useState("");
  const [hasUserDraftChanges, setHasUserDraftChanges] = useState(false);
  const [ownershipBuilderModalOpen, setOwnershipBuilderModalOpen] =
    useState(false);
  const [emailPromptOpen, setEmailPromptOpen] = useState(false);
  const [emailPromptValue, setEmailPromptValue] = useState("");
  const [emailPromptError, setEmailPromptError] = useState("");
  const dirtyTrackingReadyRef = useRef(false);
  const emailPromptResolveRef = useRef(null);
  const emailPromptInputRef = useRef(null);
  const pendingDraftChangesRef = useRef(false);
  const pendingLeaveActionRef = useRef(null);
  const allowNextNavigationRef = useRef(false);
  const browserBackGuardArmedRef = useRef(false);
  const [leavePromptOpen, setLeavePromptOpen] = useState(false);
  const [leavePromptSaving, setLeavePromptSaving] = useState(false);

  useLayoutEffect(() => {
    if (!loading) return;
    showOverlay({ title: "Loading…", subtitle: "", loaderStyle: "dots", bg: "transparent" });
    return () => hideOverlay();
  }, [loading, showOverlay, hideOverlay]);

  const [savingMode, setSavingMode] = useState("");
  // "" | "draft" | "complete"
  const [err, setErr] = useState("");

  // Generate PDF
  // ---- Industry risk result (lifted from KycSectionRiskAssessment) ----
  const [industryResult, setIndustryResult] = useState(null);
  const [geoResult, setGeoResult] = useState(null);

  const markDraftDirty = useCallback(() => {
    if (dirtyTrackingReadyRef.current) {
      setHasUserDraftChanges(true);
    }
  }, []);

  // Stable callbacks
  const handleIndustryResult = useCallback((payload, { markDirty = true } = {}) => {
    if (markDirty) markDraftDirty();
    setIndustryResult(payload);
    setForm((prev) => ({
      ...prev,
      internalUse: {
        ...(prev.internalUse || {}),
        industryRiskResult: payload,
      },
    }));
  }, [markDraftDirty]);

  // Geography is recalculated asynchronously on load, without a user edit.
  const handleGeoResult = useCallback((payload) => {
    setGeoResult(payload);
    setForm((prev) => ({
      ...prev,
      internalUse: {
        ...(prev.internalUse || {}),
        geoRiskResult: payload,
        geoRiskLevel: payload?.geoRiskLevel || null,
      },
    }));
  }, []);

  // ---------------------------
  // FIRESTORE LOGOUT
  // ---------------------------
  async function handleLogout() {
    sessionStorage.setItem("mykyc:loggedOut", "1");
    await signOut(auth);
    nav("/login", { replace: true });
  }

  function requestLogout() {
    requestLeave(handleLogout);
  }

  // ---- email sending to client ----
  const [sendingInvite, setSendingInvite] = useState(false);

  // ---- Client info (for header display) ----
  const [clientName, setClientName] = useState("");
  const [clientType, setClientType] = useState(""); // "individual" | "entity"

  // ---- Status texts (inbox-style) ----
  const [kycFormStatusText, setKycFormStatusText] = useState("");
  const [kycSubmissionId, setKycSubmissionId] = useState("");
  const [docUploadStatusText, setDocUploadStatusText] = useState("");
  const [docSubmissionId, setDocSubmissionId] = useState("");
  const [missingDocsEmailText, setMissingDocsEmailText] = useState("");

  // ---------- FORM STATE ----------
  const [form, setForm] = useState({
    engagement: {
      clientRequests: [],
      approach: {
        channel: "Direct",
        introducerName: "",
        paidIntroducer: false,
      },
      noMeetingWithContractingPartner: false,
    },

    customer: {
      type: "individual", // individual | entity
      name: "",
      nationalityOrIncorporationCountry: "",
      mainCountryOfOperation: "",
      dobOrIncorporationDate: null,
      email: "",
      phone: "",
      address: "",
      city: "",
      country: "",
      website: "",
      businessActivity: "",
    },

    uaeCompany: {
      proposedEntityNames: "",
      jurisdiction: "Mainland", // Mainland | Free zone | Other
      activities: "",
      expectedMonthlyTurnoverRange: "",
      expectedCustomerCountries: [],
      expectedSupplierCountries: [],
      expectedTransactionChannels: [],
      highRiskCountries: [],
    },

    ownership: {
      shareholders: [],
      ubos: [],
      controllers: [],
      complexStructure: false,
    },

    sourceOfFundsWealth: {
      sourceOfFunds: "",
      sourceOfFundsDetails: "",
      sourceOfWealth: [],
      sourceOfWealthDetails: "",
      netWorthCurrency: "USD",
      netWorthRange: "",
      annualIncomeCurrency: "USD",
      annualIncomeRange: "",
      annualTurnoverCurrency: "USD",
      annualTurnoverRange: "",
      sourceOfFundsTypes: [],
      thirdPartyDetails: "",
      sourceOfFundsCashDetails: "",
      sourceOfFundsOtherDetails: "",
    },

    sanctions: {
      clientDeclared: false,
      clientDetails: "",
      staffDecision: null, // "no_exposure" | "flagged" | ...
      staffAssessment: "",
    },

    pep: {
      clientDeclared: false,
      clientDetails: "",
      staffDecision: null, // "no_exposure" | "flagged" | ...
      staffAssessment: "",
    },

    pepSanctions: {
      pepDeclared: false,
      pepDetails: "",
      sanctionsDeclared: false,
      sanctionsDetails: "",
    },

    documentsChecklist: {
      parties: {},
      sow: {},
    },

    finalOverview: {
      calculatedRiskRating: "", // auto-populated ("Standard/Medium/High" or "Standard/High")
      eddRequired: null, // auto default set by FinalOverview section
      sarStatus: "no", // "no" | "yes_to_fill" | "filled"
      sarReference: "",
      relationshipAccepted: "", // "" | "yes" | "no"
      clientOnboarded: null,
      relationshipMaintained: null,
      negativeNews: false,
      profileSummary: "",
      assessment: "",
    },

    internalUse: {
      industryRiskResult: null,
      geoRiskResult: null,
      geoRiskLevel: null,
    },
  });

  const { mode: individualNameMode, ready: nameModeReady } = useNameEntryMode(form?.orgId || clientOrgId || myOrgId);

  const documentIntake = useKycDocumentIntake({ clientId: id, form, onEnsureRecord: async () => {
    const ref = doc(db, "kyc", id);
    const snapshot = await getDoc(ref);
    if (!snapshot.exists()) await setDoc(ref, removeUndefinedDeep({
      clientId: id, orgId: form.orgId || clientOrgId || myOrgId || '',
      accessScope: form.accessScope || clientAccessScope || '', ownerUid: form.ownerUid || clientOwnerUid || '',
      teamId: form.teamId || clientTeamId || '', customer: form.customer, ownership: form.ownership || {},
      documentsChecklist: form.documentsChecklist || {}, createdAt: serverTimestamp(), updatedAt: serverTimestamp(),
    }), { merge: true });
  }, onSaveTarget: async (targetId, values) => {
    const next = applyDocumentTargetValues(form, targetId, values);
    const updates = {};
    if (next.customer !== form.customer) Object.entries(next.customer).forEach(([key,value]) => {
      if (value !== form.customer?.[key]) updates[`customer.${key}`] = value;
    });
    if (next.ownership !== form.ownership) Object.entries(next.ownership || {}).forEach(([key,value]) => {
      if (value !== form.ownership?.[key]) updates[`ownership.${key}`] = value;
    });
    if (next.sourceOfFundsWealth !== form.sourceOfFundsWealth) updates.sourceOfFundsWealth = next.sourceOfFundsWealth;
    if (Object.keys(updates).length) {
      await updateDoc(doc(db, "kyc", id), { ...removeUndefinedDeep(updates), updatedAt: serverTimestamp() });
      setSavedDraftSignature(previous => {
        if (!previous) return previous;
        const baseline = JSON.parse(previous);
        const comparable = buildDraftComparable(next);
        Object.keys(updates).forEach(key => {
          const path = key.split('.');
          let destination = baseline;
          let source = comparable;
          path.slice(0,-1).forEach(segment => {
            destination[segment] = destination[segment] || {};
            destination = destination[segment]; source = source?.[segment];
          });
          const last = path[path.length-1];
          if (source?.[last] !== undefined) destination[last] = source[last];
        });
        return stableStringify(baseline);
      });
      setForm(previous => applyDocumentTargetValues(previous, targetId, values));
      setHasUserDraftChanges(true);
      if (targetId === 'customer' && values.clientName !== undefined) setClientName(values.clientName);
    }
  }});
  const approachChannel = form?.engagement?.approach?.channel || "Direct";
  const showReferralFields =
    approachChannel === "Referral" || approachChannel === "Introducer";
  const nameLabel =
    approachChannel === "Referral" ? "Referred by" : "Introduced by";
  const placeholderLabel =
    approachChannel === "Referral" ? "Name of referrer" : "Name of introducer";
  const paidLabel =
    approachChannel === "Referral" ? "Referrer paid" : "Introducer paid";

  // ---- Set variables depending on type in section KycSectionCustomerDetails
  const customerType = form?.customer?.type || "individual";
  const isEntity = customerType === "entity";
  const pepScreeningRef = useRef(null);
  const sanctionsScreeningRef = useRef(null);
  const adverseMediaScreeningRef = useRef(null);
  const [documentCompletion, setDocumentCompletion] = useState(null);
  const [pepBusy, setPepBusy] = useState(false);
  const [sanctionsBusy, setSanctionsBusy] = useState(false);
  const [adverseBusy, setAdverseBusy] = useState(false);
  const [bulkScreeningRunning, setBulkScreeningRunning] = useState(false);
  const [bulkScreeningStarted, setBulkScreeningStarted] = useState(false);
  const [bulkScreeningRequested, setBulkScreeningRequested] = useState(false);
  const bulkScreeningRunRef = useRef(0);
  const [bulkScreeningLauncherDismissed, setBulkScreeningLauncherDismissed] =
    useState(false);
  const completion = evaluateKycCompletion(form, {
    useMainCountryOfOperation,
    documentsComplete: documentCompletion?.form === form && documentCompletion?.complete === true,
    screeningBusy: bulkScreeningRunning || pepBusy || sanctionsBusy || adverseBusy,
  });

  const allScreeningsNotRun = useMemo(() => {
    const internalUse = form?.internalUse || {};
    return (
      !internalUse?.pepScreening?.result &&
      !internalUse?.sanctionsScreening?.result &&
      !internalUse?.adverseMediaScreening?.runAt
    );
  }, [form?.internalUse]);

  // Staff front sees the protected areas read-only; the sections already
  // implement the readOnly prop end-to-end (same mechanism as review views).
  const screeningReadOnly = isReadOnlyArea(myRole, PROTECTED_AREAS.SCREENING);

  // Front never gets the bulk launcher (it can't run screenings) — it goes
  // straight to the read-only sections, exactly as /kyc-view shows them.
  const showBulkScreeningCard =
    allScreeningsNotRun && !bulkScreeningLauncherDismissed && !screeningReadOnly;
  const showScreeningSections =
    !allScreeningsNotRun || bulkScreeningStarted || screeningReadOnly;

  const checklistReadOnly = isReadOnlyArea(myRole, PROTECTED_AREAS.DOCUMENTS_CHECKLIST);
  const finalOverviewReadOnly = isReadOnlyArea(myRole, PROTECTED_AREAS.FINAL_OVERVIEW);

  // Staff front saves are FIELD-MASKED: only the permitted subtrees ever leave the
  // browser, so protected compliance fields can neither be smuggled nor overwritten
  // with a stale in-memory copy (docs/staff-roles-permission-map.md §3/§5). Firestore
  // rules enforce the same boundary server-side; this keeps honest saves clean.
  const maskedKycSave = finalOverviewReadOnly;
  const maskKycPayload = useCallback(
    (payload) =>
      maskKycPayloadPure(payload, {
        masked: maskedKycSave,
        preserveCreatedAt: !!form?.createdAt,
      }),
    [maskedKycSave, form?.createdAt]
  );

  const runAllScreenings = useCallback(() => {
    if (bulkScreeningRunning) return;
    if (!canPersistScreening(myRole)) return; // read-only role: bulk launcher is disabled

    setBulkScreeningStarted(true);
    setBulkScreeningRequested(true);
    window.setTimeout(() => {
      setBulkScreeningLauncherDismissed(true);
    }, 900);
  }, [bulkScreeningRunning, myRole]);

  useEffect(() => {
    if (!bulkScreeningRequested) return;

    let cancelled = false;
    let timer;
    const runWhenReady = () => {
      if (cancelled) return;

      const runners = [
        pepScreeningRef.current?.runScreening,
        sanctionsScreeningRef.current?.runScreening,
        adverseMediaScreeningRef.current?.runScreening,
      ];

      // The sections mount after the launcher is clicked. Do not silently omit
      // a section whose imperative handle is not ready on the first frame.
      if (runners.some(runner => typeof runner !== "function")) {
        timer = window.setTimeout(runWhenReady, 50);
        return;
      }

      setBulkScreeningRequested(false);
      const runId = ++bulkScreeningRunRef.current;
      setBulkScreeningRunning(true);
      Promise.allSettled(runners.map((runner) => Promise.resolve().then(() => runner()))).finally(() => {
        // Clearing the request cleans up this effect; it must not prevent the
        // completed run from releasing the page's busy state.
        if (bulkScreeningRunRef.current === runId) setBulkScreeningRunning(false);
      });
    };
    timer = window.setTimeout(runWhenReady, 120);

    return () => {
      cancelled = true;
      window.clearTimeout(timer);
    };
  }, [bulkScreeningRequested]);

  useEffect(() => {
    bulkScreeningRunRef.current += 1;
    setBulkScreeningStarted(false);
    setBulkScreeningRequested(false);
    setBulkScreeningRunning(false);
    setBulkScreeningLauncherDismissed(false);
    return () => { bulkScreeningRunRef.current += 1; };
  }, [id]);

  // Dynamic section numbering (Ownership exists only for entities)
  const sectionNo = useMemo(() => {
    let n = 1;
    const out = {};
    out.engagement = n++; // 1
    out.customer = n++; // 2
    out.ownership = isEntity ? n++ : null; // 3 (only if entity)
    out.signatories = isEntity ? n++ : null; // 4 (only if entity)
    out.sowSof = n++;
    out.risk = n++;
    out.peps = n++;
    out.sanctions = n++;
    out.amlScreening = n++;
    out.documents = n++;
    out.finalOverview = n++;
    return out;
  }, [isEntity]);

  const visibleSectionNo = useMemo(() => {
    if (!showBulkScreeningCard || showScreeningSections) return sectionNo;
    return {
      ...sectionNo,
      documents: sectionNo.peps + 1,
      finalOverview: sectionNo.peps + 2,
    };
  }, [sectionNo, showBulkScreeningCard, showScreeningSections]);

  function setAt(path, value, { markDirty = true } = {}) {
    const currentValue = path.reduce((current, key) => current?.[key], form);
    // Some controls commit their current value on blur without an edit.
    if (stableStringify(currentValue) === stableStringify(value)) return;
    const isCalculatedRisk = path[0] === "finalOverview" &&
      ["calculatedRiskRating", "relationshipRiskResult"].includes(path[1]);
    if (markDirty && !isCalculatedRisk) markDraftDirty();
    setForm((prev) => {
      const next = structuredClone(prev);
      let ref = next;

      // create missing parents
      for (let i = 0; i < path.length - 1; i++) {
        const k = path[i];
        if (ref[k] == null || typeof ref[k] !== "object") ref[k] = {};
        ref = ref[k];
      }

      ref[path[path.length - 1]] = value;
      return next;
    });
  }

  function formWithLatestOwnershipGraph(sourceForm) {
    const nextForm =
      sourceForm && typeof sourceForm === "object" ? sourceForm : {};
    const ownership =
      nextForm.ownership && typeof nextForm.ownership === "object"
        ? nextForm.ownership
        : {};
    const accountHolderName =
      String(nextForm.customer?.name || "").trim() || "Account holder";
    const liveOwnershipGraph = buildLiveReviewOwnershipSnapshot({
      ownership,
      accountHolderName,
    });

    if (!liveOwnershipGraph?.mermaid) return nextForm;

    return {
      ...nextForm,
      ownership: {
        ...ownership,
        assistantTree: liveOwnershipGraph,
      },
    };
  }

  function buildOwnershipThresholdAssessment(sourceForm) {
    const formWithGraph = formWithLatestOwnershipGraph(sourceForm);
    const ownership =
      formWithGraph.ownership && typeof formWithGraph.ownership === "object"
        ? formWithGraph.ownership
        : {};
    const shareholderRows = Array.isArray(ownership.shareholders)
      ? ownership.shareholders
      : Array.isArray(ownership.directShareholders)
      ? ownership.directShareholders
      : [];
    const uboRows = Array.isArray(ownership.ubos)
      ? ownership.ubos
      : Array.isArray(ownership.controllingPersonsNatural)
      ? ownership.controllingPersonsNatural
      : [];

    const accountHolderName =
      String(formWithGraph.customer?.name || "").trim() || "Account holder";
    const apiPayload = buildReviewOwnershipApiPayload({
      ownership,
      accountHolderName,
      liveOwnershipGraph: ownership.assistantTree,
    });

    return { formWithGraph, ownership, shareholderRows, uboRows, apiPayload };
  }

  async function formWithAssessedOwnershipThresholds(sourceForm) {
    const { formWithGraph, ownership, apiPayload } =
      buildOwnershipThresholdAssessment(sourceForm);

    if (!apiPayload.initialAHType || !apiPayload.nodes.length) return formWithGraph;

    const apiResult = await assessUbosFromTree(
      apiPayload.initialAHType,
      apiPayload.nodes,
      orgJurisdiction
    );
    const signature = ownershipAssessmentEditSignature({ ...apiPayload, orgJurisdiction });
    const assessedOwnership = applyUboAssessmentResultToOwnership({
      ownership,
      apiPayload,
      apiResult,
      signature,
    });

    return {
      ...formWithGraph,
      ownership: assessedOwnership,
    };
  }

  function buildDraftComparable(sourceForm) {
    const formToPersist = formWithLatestOwnershipGraph(sourceForm);
    const normalizedForm = removeUndefinedDeep(normalizeClientKycData(normalizeKycNames(formToPersist, individualNameMode)));
    const comparable = {
      ...normalizedForm,
      orgId: sourceForm.orgId || clientOrgId || myOrgId || "",
      accessScope: sourceForm.accessScope || clientAccessScope || "",
      ownerUid: sourceForm.ownerUid || clientOwnerUid || "",
      teamId: clientTeamId || sourceForm.teamId || "",
      clientId: id,
      clientStatus: String(sourceForm?.clientStatus || "")
        .trim()
        .toLowerCase(),
    };

    delete comparable.createdAt;
    delete comparable.updatedAt;
    // These outputs can refresh after loading and are not editable form content.
    // Keep them in the saved payload, but exclude them from edit detection.
    if (comparable.internalUse) {
      comparable.internalUse = { ...comparable.internalUse };
      delete comparable.internalUse.geoRiskResult;
      delete comparable.internalUse.geoRiskLevel;
    }
    if (comparable.finalOverview) {
      comparable.finalOverview = { ...comparable.finalOverview };
      delete comparable.finalOverview.calculatedRiskRating;
      delete comparable.finalOverview.relationshipRiskResult;
    }
    return removeUndefinedDeep(comparable);
  }

  const currentDraftSignature = useMemo(
    () => stableStringify(buildDraftComparable(form)),
    [
      form,
      clientOrgId,
      clientAccessScope,
      clientOwnerUid,
      clientTeamId,
      myOrgId,
      individualNameMode,
      id,
    ]
  );

  const hasDraftChanges =
    hasUserDraftChanges &&
    !!savedDraftSignature &&
    currentDraftSignature !== savedDraftSignature;

  // A touched control alone is not an unsaved change; reverting an edit is clean too.
  const shouldWarnUnsavedKycChanges = hasDraftChanges || documentIntake.pending.length > 0;
  pendingDraftChangesRef.current = shouldWarnUnsavedKycChanges;

  function hasPendingUnsavedKycChanges() {
    return pendingDraftChangesRef.current;
  }

  useEffect(() => {
    allowNextNavigationRef.current = false;
  }, [location.pathname, location.search]);

  useEffect(() => {
    if (!shouldWarnUnsavedKycChanges) return undefined;

    const currentUrl =
      window.location.pathname + window.location.search + window.location.hash;

    if (!browserBackGuardArmedRef.current) {
      window.history.pushState(
        { mykycUnsavedKycGuard: true },
        "",
        currentUrl
      );
      browserBackGuardArmedRef.current = true;
    }

    const handlePopState = () => {
      if (!hasPendingUnsavedKycChanges() || allowNextNavigationRef.current) {
        browserBackGuardArmedRef.current = false;
        return;
      }

      const protectedUrl =
        location.pathname + location.search + window.location.hash;

      window.history.pushState(
        { mykycUnsavedKycGuard: true },
        "",
        protectedUrl
      );

      pendingLeaveActionRef.current = () => {
        allowNextNavigationRef.current = true;
        browserBackGuardArmedRef.current = false;
        window.history.go(-2);
      };
      setLeavePromptOpen(true);
    };

    window.addEventListener("popstate", handlePopState);
    return () => window.removeEventListener("popstate", handlePopState);
  }, [shouldWarnUnsavedKycChanges, location.pathname, location.search]);

  function requestLeave(action) {
    if (!hasPendingUnsavedKycChanges()) {
      action?.();
      return;
    }

    pendingLeaveActionRef.current = action;
    setLeavePromptOpen(true);
  }

  function closeLeavePrompt() {
    pendingLeaveActionRef.current = null;
    setLeavePromptOpen(false);
    setLeavePromptSaving(false);
  }

  function leaveWithoutSaving() {
    const action = pendingLeaveActionRef.current;
    closeLeavePrompt();
    allowNextNavigationRef.current = true;
    action?.();
  }

  async function saveDraftAndLeave() {
    const action = pendingLeaveActionRef.current;
    try {
      setLeavePromptSaving(true);
      await persistKycDraft({ complete: false });
      closeLeavePrompt();
      allowNextNavigationRef.current = true;
      action?.();
    } catch (e) {
      setErr(e?.message || "Save failed");
      setLeavePromptSaving(false);
    }
  }

  useEffect(() => {
    if (!shouldWarnUnsavedKycChanges) return undefined;

    const handleBeforeUnload = (event) => {
      if (!hasPendingUnsavedKycChanges() || allowNextNavigationRef.current) return;
      event.preventDefault();
      event.returnValue = "";
    };

    window.addEventListener("beforeunload", handleBeforeUnload);
    return () => window.removeEventListener("beforeunload", handleBeforeUnload);
  }, [shouldWarnUnsavedKycChanges]);

  // Live UBO re-assessment after ownership edits (shared with the client and review pages).
  const { failure: liveUboCheckFailure } = useLiveUboAssessment({ form, setForm, orgJurisdiction, enabled: !loading });

  function addRow(listPath, keys) {
    markDraftDirty();
    setForm((prev) => {
      const next = structuredClone(prev);
      let ref = next;
      for (const p of listPath) ref = ref[p];
      ref.push(emptyRow(keys));
      return next;
    });
  }

  function removeRow(listPath, index) {
    markDraftDirty();
    setForm((prev) => {
      const next = structuredClone(prev);
      let ref = next;
      for (const p of listPath) ref = ref[p];
      ref.splice(index, 1);
      return next;
    });
  }

  const reveal = {
    initial: { opacity: 0, y: 0, x: 10 },
    animate: { opacity: 1, y: 0, x: 0 },
    exit: { opacity: 0, y: 4 },
    transition: { duration: 0.22, ease: [0.22, 1, 0.36, 1] },
  };

  // ---------- LoadMe -----------
  async function loadMe() {
    try {
      setMeLoading(true);
      setErr("");

      const u = auth.currentUser;
      if (!u) throw new Error("You must be signed in.");

      const snap = await getDoc(doc(db, "users", u.uid));
      if (!snap.exists())
        throw new Error("User profile not found (users/{uid}).");

      const data = snap.data();

      if (data?.disabled) {
        throw new Error("Your account is disabled. Please contact an admin.");
      }

      const cleanRole = normalizeRole(data?.role);
      const admin = cleanRole === "admin" || !!data?.isAdmin;
      setMyRole(cleanRole);
      setMyTeamId(String(data?.teamId || "").trim());
      setIsAdmin(admin);

      if (admin) {
        setMyOrgId(""); // admin = no org restriction
        return;
      }

      if (!data?.orgId) {
        throw new Error("No organisation assigned to your account.");
      }

      setMyOrgId(String(data.orgId));
    } catch (e) {
      setIsAdmin(false);
      setMyOrgId("");
      setMyRole("staff");
      setMyTeamId("");
      setErr(e?.message || "Failed to load user profile");
    } finally {
      setMeLoading(false);
    }
  }

  // Clear fields when not Referral / Introducer
  useEffect(() => {
    if (
      form.engagement.approach.channel !== "Referral" &&
      form.engagement.approach.channel !== "Introducer"
    ) {
      setAt(["engagement", "approach", "introducerName"], "");
      setAt(["engagement", "approach", "paidIntroducer"], false);
    }
    // eslint-disable-next-line
  }, [form.engagement.approach.channel]);

  // ---------- LOAD CLIENT + EXISTING KYC ----------
  useEffect(() => {
    loadMe();
  }, []);

  // ---- Status listeners (inbox-style) ----
  function fmtStatusDate(ts) {
    if (!ts) return "";
    const d = ts?.toDate ? ts.toDate() : new Date(ts);
    if (isNaN(d)) return "";
    return d.toLocaleDateString(undefined, { day: "numeric", month: "short", year: "numeric" });
  }

  useEffect(() => {
    if (!id || meLoading) return;

    const scopeOrgId = String(form?.orgId || clientOrgId || myOrgId || "").trim();
    const scopeAccessScope = String(form?.accessScope || clientAccessScope || "").trim();
    const scopeTeamId = String(clientTeamId || form?.teamId || myTeamId || "").trim();
    const scopeOwnerUid = String(form?.ownerUid || clientOwnerUid || auth.currentUser?.uid || "").trim();

    function scopedAccessFilters(baseFilters = []) {
      if (isAdmin) return baseFilters;
      if (!scopeOrgId) return null;
      if (myRole === "manager") return [where("orgId", "==", scopeOrgId), ...baseFilters];

      if (scopeAccessScope === "team" && scopeTeamId) {
        return [
          where("orgId", "==", scopeOrgId),
          where("accessScope", "==", "team"),
          where("teamId", "==", scopeTeamId),
          ...baseFilters,
        ];
      }

      if (scopeAccessScope === "individual" && scopeOwnerUid) {
        return [
          where("orgId", "==", scopeOrgId),
          where("accessScope", "==", "individual"),
          where("ownerUid", "==", scopeOwnerUid),
          ...baseFilters,
        ];
      }

      return null;
    }

    // clients/{id} — KYC form sent status
    const unsubClient = onSnapshot(doc(db, "clients", id), (snap) => {
      if (!snap.exists()) return;
      const c = snap.data();
      const stage = String(c?.kycFlowStage || "");
      if (stage === "sent" && c.kycInviteSentAt) {
        setKycFormStatusText(`KYC form sent to client on ${fmtStatusDate(c.kycInviteSentAt)}`);
      } else if (stage !== "submitted") {
        setKycFormStatusText("");
      }
    });

    // kyc_submissions — KYC submitted status (overrides sent)
    const subFilters = scopedAccessFilters([where("clientId", "==", id), limit(1)]);
    const unsubSub = subFilters
      ? onSnapshot(query(collection(db, "kyc_submissions"), ...subFilters), (snap) => {
          if (snap.empty) return;
          const subDoc = snap.docs[0];
          const sub = subDoc.data();
          if (sub?.submittedAt) {
            setKycFormStatusText(`KYC submitted by client on ${fmtStatusDate(sub.submittedAt)}`);
            setKycSubmissionId(subDoc.id);
          }
        })
      : () => {};

    // documentSubmissions — upload link sent or docs submitted
    const docFilters = scopedAccessFilters([
      where("clientId", "==", id),
      where("type", "==", "documents"),
      limit(5),
    ]);
    const unsubDocs = docFilters
      ? onSnapshot(query(collection(db, "documentSubmissions"), ...docFilters), (snap) => {
          if (snap.empty) { setDocUploadStatusText(""); setDocSubmissionId(""); return; }
          const submittedDoc = snap.docs.find((d) => d.data().status === "pending_review");
          const sentDoc = snap.docs.find((d) => d.data().status === "awaiting_client");
          if (submittedDoc) {
            setDocSubmissionId(submittedDoc.id);
            setDocUploadStatusText(`Documents submitted by client on ${fmtStatusDate(submittedDoc.data().updatedAt)}`);
          } else if (sentDoc) {
            setDocSubmissionId("");
            setDocUploadStatusText(`Documents upload link sent to client on ${fmtStatusDate(sentDoc.data().updatedAt)}`);
          } else {
            setDocSubmissionId("");
            setDocUploadStatusText("");
          }
        })
      : () => {};

    // email_logs — missing docs request email sent
    const emailFilters = [
      ...(isAdmin ? [] : scopeOrgId ? [where("orgId", "==", scopeOrgId)] : []),
      where("clientId", "==", id),
      where("type", "==", "documents_request"),
      orderBy("sentAt", "desc"),
      limit(1),
    ];
    const unsubEmailLogs = isAdmin || scopeOrgId
      ? onSnapshot(query(collection(db, "email_logs"), ...emailFilters), (snap) => {
          if (snap.empty) { setMissingDocsEmailText(""); return; }
          const log = snap.docs[0].data();
          setMissingDocsEmailText(log?.sentAt ? `Email sent to client on ${fmtStatusDate(log.sentAt)}` : "");
        })
      : () => {};

    return () => { unsubClient(); unsubSub(); unsubDocs(); unsubEmailLogs(); };
    // eslint-disable-next-line
  }, [
    id,
    meLoading,
    isAdmin,
    myOrgId,
    myRole,
    myTeamId,
    clientOrgId,
    clientAccessScope,
    clientOwnerUid,
    clientTeamId,
    form?.orgId,
    form?.accessScope,
    form?.ownerUid,
    form?.teamId,
  ]);

  useEffect(() => {
    let alive = true;

    async function load() {
      let waitingForMe = false;
      try {
        setErr("");
        setLoading(true);

        // ✅ wait until user profile loaded
        if (meLoading) {
          waitingForMe = true;
          return;
        }

        // ✅ staff must have orgId, admin doesn't need it
        if (!isAdmin && !myOrgId) {
          throw new Error("No organisation assigned to your account.");
        }

        // 1) load client (clients/{id})
        const clientSnap = await getDoc(doc(db, "clients", id));
        if (!alive) return;

        if (!clientSnap.exists()) {
          setClientName("");
          setClientType("");
          throw new Error("Client not found.");
        }

        const c = clientSnap.data();
        const status = String(c?.status || "")
          .trim()
          .toLowerCase();

        const name = c?.clientName || c?.name || "";
        const typeRaw = c?.type || c?.clientType || c?.customerType || "";
        const normalizedType = normalizeClientType(typeRaw);

        setClientName(name);
        setClientType(normalizedType);

        // ✅ store orgId of the client
        const cOrgId = String(c?.orgId || "");
        const cAccessScope = String(c?.accessScope || "").trim();
        const cOwnerUid = String(c?.ownerUid || "").trim();
        const cTeamId = String(c?.teamId || "").trim();
        const myUid = String(auth.currentUser?.uid || "").trim();
        setClientOrgId(cOrgId);
        setClientAccessScope(cAccessScope);
        setClientOwnerUid(cOwnerUid);
        setClientTeamId(cTeamId);
        setOrgPreferredPhoneCountry("");
        setOrgJurisdiction(null);
        setUseMainCountryOfOperation(false);
        setRelationshipRiskSettings(sanitizeRelationshipRiskSettings(DEFAULT_RELATIONSHIP_RISK_SETTINGS));
        setSanctionsScreeningSources(DEFAULT_SANCTIONS_SCREENING_SOURCES);

        // ✅ security check: staff can't open other org clients
        if (!isAdmin && cOrgId && cOrgId !== myOrgId) {
          throw new Error(
            "Access denied: this client belongs to another organisation."
          );
        }

        if (
          !isAdmin &&
          myRole !== "manager" &&
          !(
            (cAccessScope === "team" && myTeamId && cTeamId === myTeamId) ||
            (cAccessScope === "individual" && cOwnerUid === myUid)
          )
        ) {
          throw new Error(
            "Access denied: this client belongs to another staff member."
          );
        }

        if (cOrgId) {
          const [
            orgSnap,
            countryDefaultSnap,
            countryOrgSnap,
            relationshipDefaultSnap,
            relationshipOrgSnap,
          ] = await Promise.all([
            getDoc(doc(db, "orgs", cOrgId)),
            getDoc(doc(db, "countryRisk", "lists")),
            getDoc(doc(db, "countryRisk", cOrgId)),
            getDoc(doc(db, RELATIONSHIP_RISK_SETTINGS_COLLECTION, RELATIONSHIP_RISK_DEFAULT_DOC_ID)),
            getDoc(doc(db, RELATIONSHIP_RISK_SETTINGS_COLLECTION, cOrgId)),
          ]);
          if (!alive) return;
          const orgData = orgSnap.exists() ? orgSnap.data() : null;
          setOrgJurisdiction(orgData ? (orgData.geo || "uae") : null);
          setOrgPreferredPhoneCountry(
            orgData ? orgGeoToPreferredCountry(orgData?.geo) : ""
          );
          setSanctionsScreeningSources(
            sanctionsSourcesForJurisdiction(orgData?.geo)
          );
          setUseMainCountryOfOperation(
            countryRiskUsesMainCountryOfOperation(
              countryOrgSnap.exists() ? countryOrgSnap.data() : {},
              countryDefaultSnap.exists() ? countryDefaultSnap.data() : {}
            )
          );
          setRelationshipRiskSettings(
            effectiveRelationshipRiskSettings(
              relationshipOrgSnap.exists() ? relationshipOrgSnap.data() : null,
              relationshipDefaultSnap.exists() ? relationshipDefaultSnap.data() : null
            )
          );
        }

        // ✅ sync client -> form.customer (+ force customer.type)
        setForm((prev) => ({
          ...prev,
          orgId: cOrgId || prev.orgId || "",
          clientStatus: status,
          customer: {
            ...prev.customer,
            ...clientToKycCustomer(c),
            type: normalizedType || prev.customer.type,
          },
        }));

        // 2) load KYC (kyc/{id})
        const kycSnap = await getDoc(doc(db, "kyc", id));
        if (!alive) return;

        if (kycSnap.exists()) {
          const data = normalizeClientKycData(kycSnap.data());

          // ✅ remove legacy fields BEFORE merging into state
          const sowLoaded = data.sourceOfFundsWealth || {};
          // eslint-disable-next-line no-unused-vars
          const { supportingDocuments, ...sowLoadedClean } = sowLoaded;

          // eslint-disable-next-line no-unused-vars
          const { documents, ...dataClean } = data;

          setForm((prev) => ({
            ...prev,
            ...dataClean,

            engagement: { ...prev.engagement, ...(dataClean.engagement || {}) },

            customer: {
              ...prev.customer,
              ...(dataClean.customer || {}),
              type:
                normalizeClientType(dataClean.customer?.type) ||
                prev.customer.type,
              dobOrIncorporationDate: toDateOrNull(
                dataClean.customer?.dobOrIncorporationDate
              ),
            },

            uaeCompany: { ...prev.uaeCompany, ...(dataClean.uaeCompany || {}) },
            ownership: { ...prev.ownership, ...(dataClean.ownership || {}) },

            // ✅ clean merge (no supportingDocuments)
            sourceOfFundsWealth: {
              ...prev.sourceOfFundsWealth,
              ...(data.sourceOfFundsWealth || {}),

              // ✅ always store as array (SoW checkboxes)
              sourceOfWealth: Array.isArray(
                data.sourceOfFundsWealth?.sourceOfWealth
              )
                ? data.sourceOfFundsWealth.sourceOfWealth
                : [],

              // ✅ always store as array (SoF MultiSelect)
              sourceOfFundsTypes: Array.isArray(
                data.sourceOfFundsWealth?.sourceOfFundsTypes
              )
                ? data.sourceOfFundsWealth.sourceOfFundsTypes
                : [],
            },

            sanctions: { ...prev.sanctions, ...(dataClean.sanctions || {}) },

            pep: { ...(prev.pep || {}), ...(dataClean.pep || {}) },

            pepSanctions: {
              ...prev.pepSanctions,
              ...(dataClean.pepSanctions || {}),
            },

            internalUse: {
              ...prev.internalUse,
              ...(dataClean.internalUse || {}),
            },

            documentsChecklist: {
              ...(prev.documentsChecklist || {}),
              ...(dataClean.documentsChecklist || {}),
              parties: {
                ...(prev.documentsChecklist?.parties || {}),
                ...(dataClean.documentsChecklist?.parties || {}),
              },
              sow: {
                ...(prev.documentsChecklist?.sow || {}),
                ...(dataClean.documentsChecklist?.sow || {}),
              },
            },

            finalOverview: {
              ...(prev.finalOverview || {}),
              ...(dataClean.finalOverview || {}),
            },

            clientStatus: String(
              dataClean?.clientStatus || prev.clientStatus || status || ""
            )
              .trim()
              .toLowerCase(),
          }));
        }
      } catch (e) {
        setErr(e?.message || "Failed to load KYC");
      } finally {
        if (alive && !waitingForMe) setLoading(false);
      }
    }

    load();
    return () => {
      alive = false;
    };
  }, [id, meLoading, isAdmin, myOrgId, myRole, myTeamId]);

  useEffect(() => {
    dirtyTrackingReadyRef.current = false;
    pendingDraftChangesRef.current = false;
    setHasUserDraftChanges(false);
    if (loading) return undefined;

    const readyTimer = window.setTimeout(() => {
      dirtyTrackingReadyRef.current = true;
    }, 900);

    return () => {
      window.clearTimeout(readyTimer);
      dirtyTrackingReadyRef.current = false;
    };
  }, [loading, id]);

  useEffect(() => {
    if (loading || savedDraftSignature) return;
    const timer = window.setTimeout(() => {
      setSavedDraftSignature(currentDraftSignature);
    }, 450);
    return () => window.clearTimeout(timer);
  }, [loading, savedDraftSignature, currentDraftSignature]);

  // --- TOAST DISPLAY AFTER REDIRECTION from review approved ---
  const toastRef = useRef(null);
  const generalUpload = useKycDocumentDrop(documentIntake.start);
  const checklistIntakeBusy = documentIntake.session?.busy && documentIntake.session?.dropContext?.source === 'checklist';
  const clientInformationIntakeBusy = documentIntake.session?.busy && !checklistIntakeBusy;
  const intakeModalOpen = Boolean(documentIntake.session && !documentIntake.session.busy);

  useEffect(() => {
    const t = location.state?.toast;
    const toastId = location.state?.toastId;
    if (!t || !toastId) return;

    const key = `toast_consumed_${toastId}`;
    if (sessionStorage.getItem(key)) return;
    sessionStorage.setItem(key, "1");

    toastRef.current?.show(t);

    // clear state so it doesn't re-trigger
    nav(".", { replace: true, state: null });
  }, [location.state, nav, id]);

  useEffect(() => {
    if (!emailPromptOpen) return undefined;
    const timer = window.setTimeout(() => {
      emailPromptInputRef.current?.focus();
    }, 80);
    return () => window.clearTimeout(timer);
  }, [emailPromptOpen]);

  function openEmailPrompt(initialValue = "") {
    setEmailPromptValue(initialValue);
    setEmailPromptError("");
    setEmailPromptOpen(true);

    return new Promise((resolve) => {
      emailPromptResolveRef.current = resolve;
    });
  }

  function closeEmailPrompt(value = null) {
    setEmailPromptOpen(false);
    const resolve = emailPromptResolveRef.current;
    emailPromptResolveRef.current = null;
    resolve?.(value);
  }

  function submitEmailPrompt() {
    const nextEmail = String(emailPromptValue || "").trim();
    if (!nextEmail) {
      setEmailPromptError("Enter the client email address to send the form link.");
      return;
    }
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(nextEmail)) {
      setEmailPromptError("Enter a valid email address.");
      return;
    }
    closeEmailPrompt(nextEmail);
  }

  // ---------- HANDLE SEND EMAIL TO CLIENT ----------
  async function handleSendFormToClient() {
    try {
      setErr("");

      let email = (form?.customer?.email || "").trim();

      if (!email) {
        email = await openEmailPrompt();
        if (!email) return;
        email = email.trim();
      }

      if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
        toastRef.current?.show({
          severity: "warn",
          summary: "Invalid email",
          detail: "Please enter a valid client email address.",
          life: 3500,
        });
        return;
      }

      setSendingInvite(true);

      // keep state aligned
      setAt(["customer", "email"], email);

      // ✅ 0) get firebase token (required by new server.js)
      const u = auth.currentUser;
      if (!u) throw new Error("You must be signed in.");

      const idToken = await u.getIdToken();

      // ✅ 1) SAVE CURRENT KYC FORM FIRST
      if (!nameModeReady) throw new Error("Organisation settings are still loading. Please try again.");
    const formToPersist = await formWithAssessedOwnershipThresholds(form);
      const normalizedForm = removeUndefinedDeep(normalizeClientKycData(normalizeKycNames(formToPersist, individualNameMode)));
      const payload = {
        ...normalizedForm,
        orgId: form.orgId || clientOrgId || myOrgId || "",
        accessScope: form.accessScope || clientAccessScope || "",
        ownerUid: form.ownerUid || clientOwnerUid || "",
        teamId: clientTeamId || form.teamId || "",
        customer: { ...normalizedForm.customer, email }, // ensure email included
        clientId: id,
        clientStatus: String(form?.clientStatus || "")
          .trim()
          .toLowerCase(),
        updatedAt: serverTimestamp(),
        createdAt: form?.createdAt || serverTimestamp(),
      };

      await setDoc(doc(db, "kyc", id), maskKycPayload(payload), { merge: true });

      // ✅ 2) update client flow status (same as before)
      await updateDoc(doc(db, "clients", id), {
        ...kycCustomerToClientFields({ ...normalizedForm.customer, email }),
        kycInviteEmail: email,
        kycInviteSentAt: serverTimestamp(),
        kycFlowStage: "sent",
      });

      // ✅ 3) call NEW staff endpoint (secured)
      await documentIntake.savePending();
      const resp = await fetch(joinUrl(SERVER_URL, "/myKyc/staff/kyc-link/send"), {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${idToken}`,
        },
        body: JSON.stringify({ clientId: id, email }),
      });

      let json = {};
      let rawText = "";

      try {
        rawText = await resp.text();
        json = rawText ? JSON.parse(rawText) : {};
      } catch (e) {
        // ignore parse error
      }

      if (!resp.ok || json?.ok !== true) {
        throw new Error(
          json?.error ||
            rawText ||
            `Failed to send email (HTTP ${resp.status} ${resp.statusText})`
        );
      }

      // ✅ 4) redirect + toast
      allowNextNavigationRef.current = true;
      nav("/clients", {
        replace: true,
        state: {
          toastId: crypto?.randomUUID?.() || String(Date.now()),
          toast: {
            severity: "success",
            summary: "Email sent",
            detail: `KYC saved and form link sent to ${email}.`,
            life: 3000,
          },
        },
      });
    } catch (e) {
      setErr(e?.message || "Failed to send email link");
    } finally {
      setSendingInvite(false);
    }
  }

  async function persistKycDraft({ complete = false } = {}) {
    if (maskedKycSave && complete) {
      throw new Error("Marking KYC as complete is a compliance action reserved for Staff compliance.");
    }
    if (!nameModeReady) throw new Error("Organisation settings are still loading. Please try again.");
    if (complete && (!completion.complete || pepScreeningRef.current?.isLoading ||
        sanctionsScreeningRef.current?.isLoading || adverseMediaScreeningRef.current?.isLoading)) {
      throw new Error("Complete all required information, screenings, final outcomes and the document checklist before marking KYC as complete.");
    }
    // A draft preserves work in progress, including incomplete ownership data.
    // Final completion still requires the assessment call to succeed.
    // A draft saved right after an ownership edit (before the live check returned) gets a fresh
    // UBO check, so it never stores results for the previous ownership; if that check fails,
    // the draft is still saved and the next change re-runs it.
    let formToPersist;
    if (complete) formToPersist = await formWithAssessedOwnershipThresholds(form);
    else if (orgJurisdiction !== null && !storedUboAssessmentIsCurrent(form, orgJurisdiction)) {
      formToPersist = await formWithAssessedOwnershipThresholds(form).catch(() => formWithLatestOwnershipGraph(form));
    } else formToPersist = formWithLatestOwnershipGraph(form);
    const normalizedForm = removeUndefinedDeep(normalizeClientKycData(normalizeKycNames(formToPersist, individualNameMode)));
    const payload = {
      ...normalizedForm,
      orgId: form.orgId || clientOrgId || myOrgId || "",
      accessScope: form.accessScope || clientAccessScope || "",
      ownerUid: form.ownerUid || clientOwnerUid || "",
      teamId: clientTeamId || form.teamId || "",
      clientId: id,
      clientStatus: String(form?.clientStatus || "")
        .trim()
        .toLowerCase(),
      updatedAt: serverTimestamp(),
      createdAt: form?.createdAt || serverTimestamp(),
    };

    await setDoc(doc(db, "kyc", id), maskKycPayload(payload), { merge: true });

    const clientPatch = maskedKycSave
      ? // Front saves mirror only ordinary customer fields; completion state and
        // its timestamp belong to compliance and are left untouched.
        { ...kycCustomerToClientFields(form.customer) }
      : {
          ...kycCustomerToClientFields(form.customer),
          kycCompleted: !!complete,
          lastKycDate: complete ? serverTimestamp() : null,
        };

    await setDoc(doc(db, "clients", id), clientPatch, { merge: true });
    await documentIntake.savePending();
    setSavedDraftSignature(currentDraftSignature);
    pendingDraftChangesRef.current = false;
    setHasUserDraftChanges(false);
  }

  // ---------- HANDLE SAVE ----------
  async function saveKyc({ complete = false } = {}) {
    try {
      setErr("");
      setSavingMode(complete ? "complete" : "draft");
      await persistKycDraft({ complete });

      allowNextNavigationRef.current = true;
      nav("/clients", {
        replace: true,
        state: {
          toastId: crypto?.randomUUID?.() || String(Date.now()),
          toast: {
            severity: "success",
            summary: complete ? "KYC completed" : "Draft saved",
            detail: complete
              ? `"${clientName || "Client"}" was saved as complete.`
              : `"${clientName || "Client"}" was saved as draft.`,
            life: 2500,
          },
        },
      });
    } catch (e) {
      setErr(e?.message || "Save failed");
    } finally {
      setSavingMode("");
    }
  }

  const shareholderKeys = useMemo(
    () => ["name", "type", "country", "percent", "idOrRegNo"],
    []
  );
  const uboKeys = useMemo(
    () => [
      "fullName",
      "nationality",
      "dob",
      "basisOfControl",
      "effectiveOwnershipPercent",
    ],
    []
  );
  const controllerKeys = useMemo(
    () => ["name", "role", "nationality", "countryOfResidence", "idNo"],
    []
  );

  // ------------------------------------------------------------------------------------------------
  // ✅ TOP NAV across KYC sections (scroll + active highlight)
  // ------------------------------------------------------------------------------------------------

  const KYC_NAV_ITEMS = useMemo(() => {
    const pad2 = (n) => String(n || "").padStart(2, "0");

    const items = [
      {
        id: "kyc-sec-engagement",
        num: pad2(sectionNo.engagement),
        label: "Engagement",
      },
      {
        id: "kyc-sec-customer",
        num: pad2(sectionNo.customer),
        label: "Customer",
      },
    ];

    if (isEntity && sectionNo.ownership) {
      items.push({
        id: "kyc-sec-ownership",
        num: pad2(sectionNo.ownership),
        label: "Ownership",
      });
    }

    if (isEntity && sectionNo.signatories) {
      items.push({
        id: "kyc-sec-signatories",
        num: pad2(sectionNo.signatories),
        label: "Signatories",
      });
    }

    items.push(
      { id: "kyc-sec-sowsof", num: pad2(sectionNo.sowSof), label: "SoW / SoF" },
      { id: "kyc-sec-risk", num: pad2(sectionNo.risk), label: "Risk" }
    );

    if (showBulkScreeningCard && !showScreeningSections) {
      items.push({
        id: "kyc-sec-screenings",
        num: pad2(sectionNo.peps),
        label: "Screenings",
      });
    } else {
      items.push(
        { id: "kyc-sec-pep", num: pad2(sectionNo.peps), label: "PEP" },
        {
          id: "kyc-sec-sanctions",
          num: pad2(sectionNo.sanctions),
          label: "Sanctions",
        },
        {
          id: "kyc-sec-adverse",
          num: pad2(sectionNo.amlScreening),
          label: "Adverse media",
        }
      );
    }

    items.push(
      {
        id: "kyc-sec-docs",
        num: pad2(visibleSectionNo.documents),
        label: "Documents",
      },
      {
        id: "kyc-sec-final",
        num: pad2(visibleSectionNo.finalOverview),
        label: "Final",
      }
    );

    return items;
  }, [
    sectionNo,
    visibleSectionNo,
    isEntity,
    showBulkScreeningCard,
    showScreeningSections,
  ]);

  const [activeKycSectionId, setActiveKycSectionId] =
    useState("kyc-sec-engagement");

  useEffect(() => {
    const items = KYC_NAV_ITEMS || [];
    const ids = items.map((x) => x.id).filter(Boolean);

    if (!ids.length) return;

    const TOP_OFFSET = 130; // adjust if you have a sticky header/top nav height

    let raf = 0;

    const computeActive = () => {
      // Pick the last section whose top is above the "reading line"
      // (reading line = top of viewport + offset)
      const line = TOP_OFFSET;

      let bestId = ids[0];
      let bestTop = -Infinity;

      for (const id of ids) {
        const el = document.getElementById(id);
        if (!el) continue;

        const rect = el.getBoundingClientRect();
        const top = rect.top;

        if (top <= line && top > bestTop) {
          bestTop = top;
          bestId = id;
        }
      }

      setActiveKycSectionId(bestId);
    };

    const onScrollOrResize = () => {
      cancelAnimationFrame(raf);
      raf = requestAnimationFrame(computeActive);
    };

    window.addEventListener("scroll", onScrollOrResize, { passive: true });
    window.addEventListener("resize", onScrollOrResize);

    // run once on mount
    onScrollOrResize();

    return () => {
      cancelAnimationFrame(raf);
      window.removeEventListener("scroll", onScrollOrResize);
      window.removeEventListener("resize", onScrollOrResize);
    };
  }, [KYC_NAV_ITEMS]);

  // ------------------------------------------------------------------------------------------------
  // -------------------------------- RETURN FUNCTION (RENDER) --------------------------------------
  // ------------------------------------------------------------------------------------------------

  useStaffTopBand(
    {
      title: (
        <motion.span
          key="kyc-form-title"
          initial={{ opacity: 0, y: 4 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: -4 }}
          transition={{ duration: 0.22, ease: "easeInOut" }}
        >
          KYC · Edit
        </motion.span>
      ),
      contentClassName: "kycPartClientInfo",
      smallBandColor: "#8fa8bb",
      titleColor: "#ffffff",
      right: (
        <div className="d-flex align-items-center gap-2 kycTitleActions">
          {clientName ? (
            <div className="kycTitleClientBlock">
              <span className="kycTitleClientName">{clientName}</span>
              {clientType && (
                <span
                  className={
                    "kycTypeBadge " +
                    (clientType === "entity"
                      ? "kycTypeBadgeEntity"
                      : "kycTypeBadgeIndividual")
                  }
                >
                  {typeLabel(clientType)}
                </span>
              )}
            </div>
          ) : null}

          <button
            type="button"
            className="btn btn-sm btn-outline-light kycInboxBackBtn"
            onClick={() => requestLeave(() => nav("/clients"))}
            style={{ whiteSpace: "nowrap" }}
            aria-label="Back to clients"
            title="Back to clients"
          >
            <img src={iconHomeA} alt="" className="kycInboxBackHomeIcon" aria-hidden="true" />
          </button>

          <button
            type="button"
            className="btn btn-sm btn-outline-light kycInboxLogoutBtn"
            onClick={requestLogout}
            style={{ whiteSpace: "nowrap" }}
          >
            Log out
          </button>
        </div>
      ),
    },
    [clientName, clientType, shouldWarnUnsavedKycChanges, nav]
  );

  if (loading) {
    return null;
  }

  return (
    <OwnershipLabelsProvider jurisdiction={orgJurisdiction}>
    <div className="kycFormPage staffShellContentPage">
      {intakeModalOpen && <KycDocumentIntakeModal intake={documentIntake} form={form} separateNames={individualNameMode === "separate"} />}
      <input
        ref={generalUpload.inputRef}
        type="file"
        accept="application/pdf,image/*"
        multiple
        hidden
        aria-label="Add KYC documents"
        onChange={generalUpload.onPickerChange}
      />
      {generalUpload.dropBounds && (
        <div className="kycGeneralDropOverlay" style={generalUpload.dropBounds} aria-hidden="true">
          <span className="kycGeneralDropLabel">
            <i className="bi bi-file-earmark-arrow-up" aria-hidden="true" />
            Drop document to identify and process
          </span>
        </div>
      )}
      {/* ✅ FIXED TOP NAV (scroll across main KYC sections) */}
      {!ownershipBuilderModalOpen ? (
        <motion.div
          initial={false}
          animate={{ opacity: intakeModalOpen ? 0 : 1 }}
          transition={{ duration: 0.2, ease: "easeOut" }}
          inert={intakeModalOpen ? true : undefined}
          aria-hidden={intakeModalOpen ? true : undefined}
        >
        <AdverseMediaTopNav
          items={KYC_NAV_ITEMS}
          activeId={activeKycSectionId}
          offset={95}
          rightActions={
            <>
              {completion.complete && !maskedKycSave && nameModeReady && <button
                className="btn amTopNavActionBtn amTopNavActionBtnComplete"
                type="button"
                disabled={!!savingMode || maskedKycSave}
                title={maskedKycSave ? "Read-only for your role — completion is a compliance decision" : undefined}
                onClick={() => saveKyc({ complete: true })}
              >
                {savingMode === "complete" ? "Saving…" : "Mark as complete"}
              </button>}
              <div
                className={`amTopNavDraftSlot${
                  shouldWarnUnsavedKycChanges ? " isVisible" : ""
                }`}
                aria-hidden={!shouldWarnUnsavedKycChanges}
              >
                <button
                  className="btn amTopNavActionBtn amTopNavActionBtnDraft"
                  type="button"
                  disabled={!!savingMode || (!hasDraftChanges && !documentIntake.pending.length)}
                  onClick={() => saveKyc({ complete: false })}
                  tabIndex={shouldWarnUnsavedKycChanges ? 0 : -1}
                >
                  {savingMode === "draft" ? "Saving…" : "Save draft"}
                </button>
              </div>
            </>
          }
        />
        </motion.div>
      ) : null}

      {/* NORMAL PAGE CONTENT */}
      <motion.div
        className="container pb-4 kycPartClientInfo"
        variants={pageAnim}
        initial="initial"
        animate="animate"
        exit="exit"
        transition={{ duration: 0.25 }}
      >
        <div className="kycTopActions">
          <div className="d-flex gap-2 flex-wrap">
            <button
              type="button"
              className="btn btn-sm kycBtnOutline"
              onClick={handleSendFormToClient}
              disabled={sendingInvite}
            >
              <i className="bi bi-send me-2" style={{ fontSize: 14 }}></i>
              {sendingInvite ? "Sending…" : "Send e-form to client"}
            </button>

            <button
              type="button"
              className="btn btn-sm kycBtnOutline"
              onClick={generalUpload.openPicker}
            >
              <i className="bi bi-file-earmark-plus me-2" aria-hidden="true" />
              Add document
            </button>
            <button
              type="button"
              className="btn btn-sm kycBtnOutline"
              disabled
              title="PDF generation is temporarily disabled"
            >
              Generate PDF
            </button>
          </div>
          {kycFormStatusText && (
            kycSubmissionId && kycFormStatusText.includes("submitted") ? (
              <button
                type="button"
                className="kycFormStatusBadge kycFormStatusBadgeBtn"
                onClick={() => requestLeave(() => nav(`/kyc-review/${kycSubmissionId}`))}
              >
                <i className="bi bi-eye me-1" style={{ fontSize: "0.7rem" }} />{kycFormStatusText}
              </button>
            ) : (
              <span className="kycFormStatusBadge">
                <i className={`bi ${kycFormStatusText.includes("submitted") ? "bi-eye" : "bi-send"} me-1`} style={{ fontSize: "0.7rem" }} />{kycFormStatusText}
              </span>
            )
          )}
        </div>

        {/* ALERT BANNER WHEN SAVED SUCCESSED OR FAILED */}
        {err && <div className="alert alert-danger">{err}</div>}

        {/* <div className="kycSectionHeadSection1">
          <h5 className="kycSectionTitle">Client Information</h5>
        </div> */}

        <form ref={generalUpload.contentRef} onSubmit={(e) => e.preventDefault()}>
          {/* ====================================== */}
          {/* START - MAIN FIRST PART - INFO CLIENT */}
          {/* ====================================== */}

          <div className="kycGroup kycGroupInfo" aria-busy={Boolean(clientInformationIntakeBusy)}>
            <div className="kycGroupHead">
              <div className="kycGroupTitle">Client information</div>
              <div className="kycGroupHint">
                Identity, ownership & financial profile
              </div>
            </div>

            <div className="kycClientInformationBody">
            <KycDocumentProcessingOverlay message={clientInformationIntakeBusy ? documentIntake.session.message : ''} onCancel={documentIntake.cancelProcessing} />
            <div className="kycClientInformationContent" inert={clientInformationIntakeBusy ? true : undefined}>
            {/* ============================ */}
            {/* A) Engagement (STAFF ONLY) */}
            {/* ============================ */}

            <section id="kyc-sec-engagement">
              <div className="card kycCard mb-3">
                <div className="kycSectionHead">
                  <h5 className="kycSectionTitle">
                    {sectionNo.engagement} | Engagement
                  </h5>
                </div>

                <div className="kycSectionBody">
                  <motion.div
                    className="row g-3 align-items-end"
                    layout
                    transition={{ duration: 0.22 }}
                  >
                    {/* Column 1 */}
                    <div className="col-5">
                      <div className="text-muted small mb-1 titleInput">
                        How the firm was approached
                      </div>
                      <select
                        className="form-select"
                        value={form.engagement.approach.channel}
                        onChange={(e) =>
                          setAt(
                            ["engagement", "approach", "channel"],
                            e.target.value
                          )
                        }
                      >
                        {APPROACH_CHANNEL_OPTIONS.map((o) => (
                          <option key={o} value={o}>
                            {o}
                          </option>
                        ))}
                      </select>
                    </div>

                    <AnimatePresence initial={false} mode="popLayout">
                      {showReferralFields ? (
                        <div key="refFields" style={{ display: "contents" }}>
                          {/* Column 2 */}
                          <motion.div
                            key="introducerCol"
                            className="col-5"
                            layout
                            initial={reveal.initial}
                            animate={reveal.animate}
                            exit={reveal.exit}
                            transition={reveal.transition}
                          >
                            <div className="text-muted small mb-1 titleInput titleReferral">
                              {nameLabel}
                            </div>

                            <input
                              className="form-control inputReferral"
                              value={form.engagement.approach.introducerName}
                              onChange={(e) =>
                                setAt(
                                  ["engagement", "approach", "introducerName"],
                                  e.target.value
                                )
                              }
                              placeholder={placeholderLabel}
                            />
                          </motion.div>

                          {/* Column 3 */}
                          <motion.div
                            key="paidCol"
                            className="col-2"
                            layout
                            initial={reveal.initial}
                            animate={reveal.animate}
                            exit={reveal.exit}
                            transition={reveal.transition}
                          >
                            <div
                              className="text-muted small mb-1 titleInput"
                              style={{ visibility: "hidden" }}
                            >
                              Paid
                            </div>

                            <div
                              className="d-flex align-items-center gap-2"
                              style={{
                                height: "calc(2.375rem + 2px)",
                                paddingLeft: 2,
                                cursor: "pointer",
                                marginLeft: 20,
                              }}
                              onClick={() =>
                                setAt(
                                  ["engagement", "approach", "paidIntroducer"],
                                  !form.engagement.approach.paidIntroducer
                                )
                              }
                            >
                              <input
                                className="form-check-input m-0"
                                type="checkbox"
                                checked={
                                  !!form.engagement.approach.paidIntroducer
                                }
                                onChange={(e) =>
                                  setAt(
                                    [
                                      "engagement",
                                      "approach",
                                      "paidIntroducer",
                                    ],
                                    e.target.checked
                                  )
                                }
                                onClick={(e) => e.stopPropagation()}
                              />
                              <span className="paidLabel">{paidLabel}</span>
                            </div>
                          </motion.div>

                          {/* NEW PRESENCE MEETING (hidden) */}
                          <div className="col-12" style={{ display: "none" }}>
                            <label className="form-check mb-0">
                              <input
                                className="form-check-input"
                                type="checkbox"
                                checked={
                                  !!form.engagement
                                    .noMeetingWithContractingPartner
                                }
                                onChange={(e) =>
                                  setAt(
                                    [
                                      "engagement",
                                      "noMeetingWithContractingPartner",
                                    ],
                                    e.target.checked
                                  )
                                }
                              />
                              <span className="form-check-label">
                                Absence of meeting with contracting partner
                              </span>
                            </label>
                          </div>
                        </div>
                      ) : null}
                    </AnimatePresence>
                  </motion.div>
                </div>
              </div>
            </section>

            {/* ====================================== */}
            {/* B) Customer identification (SHARED) */}
            {/* ====================================== */}

            <section id="kyc-sec-customer">
              <KycSectionCustomerDetails
                form={form}
                onSetAt={setAt}
                mode="staff"
                sectionNo={sectionNo.customer}
                separateNames={individualNameMode === "separate"}
                preferredPhoneCountry={orgPreferredPhoneCountry}
                useMainCountryOfOperation={useMainCountryOfOperation}
              />
            </section>

            {/* ================================ */}
            {/* D) Ownership & Control */}
            {/* ================================ */}
            {isEntity ? (
              <section id="kyc-sec-ownership">
                {liveUboCheckFailure ? (
                  <div role="status" style={{ fontSize: 13, color: "#9B2C2C", margin: "0 0 8px" }}>
                    UBO check failed: {liveUboCheckFailure} It runs again at the next ownership change.
                  </div>
                ) : null}
                <KycClientOwnershipDesktop
                  separateNames={individualNameMode === "separate"}
                  orgJurisdiction={orgJurisdiction}
                  form={form}
                  onSetAt={setAt}
                  sectionNo={sectionNo.ownership}
                  mode="client"
                  showGuidedMethod={false}
                  showManualEntryMethod={true}
                  forceApiCallInTree={true}
                  skipTutorial={true}
                  onVisualBuilderModalOpenChange={setOwnershipBuilderModalOpen}
                />
              </section>
            ) : null}

            {/* E) Authorised Signatories */}
            {/* ================================ */}
            {isEntity ? (
              <section id="kyc-sec-signatories">
                <KycClientSignatoriesDesktop
                  form={form}
                  onSetAt={setAt}
                  sectionNo={sectionNo.signatories}
                  separateNames={individualNameMode === "separate"}
                  hideHint
                />
              </section>
            ) : null}

            {/* ============================================ */}
            {/* E) Source of funds & wealth (SHARED) */}
            {/* ============================================ */}
            <section id="kyc-sec-sowsof">
              <KycSectionSowSof
                orgJurisdiction={orgJurisdiction}
                form={form}
                onSetAt={setAt}
                sectionNo={sectionNo.sowSof}
              />
            </section>

            {/* ====================================== */}
            {/* END - MAIN FIRST PART - INFO CLIENT */}
            {/* ====================================== */}
            </div>
            </div>
          </div>
          {/* ====================================== */}

          {/* ====================================== */}
          {/* START - MAIN SECOND PART - COMPLIANCE ASSESSMENT */}
          {/* ====================================== */}

          <div className="kycGroup kycGroupAssess">
            <div className="kycGroupHead">
              <div className="kycGroupTitle">Compliance Assessment</div>
              <div className="kycGroupHint">
                Screening, risk scoring, documents & decision
              </div>
            </div>

            {/* =============================== */}
            {/* NEW COUNTRY ASSESSMENT CARD */}
            {/* =============================== */}
            <section id="kyc-sec-risk">
              <KycReadOnlyBanner visible={isReadOnlyArea(myRole, PROTECTED_AREAS.RISK_ASSESSMENT)}>
                View only for your role — the compliance assessment sections are in read mode.
              </KycReadOnlyBanner>
              {isReadOnlyArea(myRole, PROTECTED_AREAS.RISK_ASSESSMENT) ? (
                <KycViewRiskAssessment form={form} sectionNo={sectionNo.risk} />
              ) : (
                <KycSectionRiskAssessment
                  form={form}
                  sectionNo={sectionNo.risk}
                  onIndustryResult={handleIndustryResult}
                  onGeoResult={handleGeoResult}
                  orgId={form.orgId || clientOrgId || myOrgId}
                />
              )}
            </section>

            {/* =============================== */}
            {/* F) Client PEP & Sanctions declaration — hidden on /kyc, visible in review pages */}

            <AnimatePresence initial={false}>
              {showBulkScreeningCard ? (
                <motion.section
                  key="combined-screening-launcher"
                  id="kyc-sec-screenings"
                  style={{ marginBottom: 14 }}
                  initial={{ opacity: 1, height: "auto", y: 0 }}
                  animate={{ opacity: 1, height: "auto", y: 0 }}
                  exit={{
                    opacity: 0,
                    height: 0,
                    y: -8,
                    marginBottom: 0,
                    overflow: "hidden",
                  }}
                  transition={{ duration: 0.28, ease: [0.22, 1, 0.36, 1] }}
                >
                  <div className="card kycCard">
                    <div className="kycSectionHead">
                      <h5 className="kycSectionTitle">
                        {sectionNo.peps} | PEP, Sanctions and Adverse Media
                        Screenings
                      </h5>
                    </div>
                    <div className="kycSectionBody">
                      <div
                        className="d-flex flex-column align-items-center justify-content-center"
                        style={{ minHeight: 118 }}
                      >
                        <button
                          type="button"
                          className="btn btn-sm btn-outline-secondary kycPepSearchBtn"
                          style={{
                            width: 260,
                            minHeight: 48,
                            padding: "8px 20px",
                            display: "inline-flex",
                            flexDirection: "column",
                            alignItems: "center",
                            justifyContent: "center",
                            gap: 4,
                            fontSize: 12,
                          }}
                          onClick={runAllScreenings}
                          disabled={bulkScreeningRunning}
                          title="Run PEP, sanctions and adverse media screenings"
                        >
                          <img
                            src={doubleTapFullIcon}
                            alt=""
                            aria-hidden="true"
                            style={{ width: 30, height: 30, flex: "0 0 auto" }}
                          />
                          Start screenings
                        </button>
                        <div className="kycScreeningAssistNote">
                          Screening results are intended to assist compliance processes and should be independently reviewed and verified
                        </div>
                      </div>
                    </div>
                  </div>
                </motion.section>
              ) : null}
            </AnimatePresence>

            <AnimatePresence initial={false}>
              {showScreeningSections ? (
                <motion.div
                  key="screening-sections"
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                  transition={{ duration: 0.18, ease: [0.22, 1, 0.36, 1] }}
                >
                  {/* =============================== */}
                  {/* G) PEP screening (STAFF) */}
                  {/* =============================== */}
                  <section id="kyc-sec-pep">
                    {screeningReadOnly ? (
                      <KycViewPep form={form} sectionNo={sectionNo.peps} />
                    ) : (
                      <KycSectionPepWithScreening
                        hideScreeningSummary // Temporary: hide only the summary, keeping subject cards visible.
                        ref={pepScreeningRef}
                        onScreeningStateChange={setPepBusy}
                        form={form}
                        readOnly={screeningReadOnly}
                        onSetAt={setAt}
                        sectionNo={sectionNo.peps}
                        initiallyExpanded={bulkScreeningStarted}
                      />
                    )}
                  </section>

                  {/* =============================== */}
                  {/* H) Sanctions screening (STAFF) */}
                  {/* =============================== */}
                  <section id="kyc-sec-sanctions">
                    {screeningReadOnly ? (
                      <KycViewSanctions
                        form={form}
                        sectionNo={sectionNo.sanctions}
                      />
                    ) : (
                      <KycSectionSanctionsWithScreening
                        ref={sanctionsScreeningRef}
                        onScreeningStateChange={setSanctionsBusy}
                        form={form}
                        readOnly={screeningReadOnly}
                        onSetAt={setAt}
                        sectionNo={sectionNo.sanctions}
                        sanctionsSources={sanctionsScreeningSources}
                        initiallyExpanded={bulkScreeningStarted}
                      />
                    )}
                  </section>

                  {/* ====================================== */}
                  {/* ADVERSE MEDIA + SCREENING */}
                  {/* ====================================== */}
                  <section id="kyc-sec-adverse">
                    {screeningReadOnly ? (
                      <KycViewAdverseMedia
                        form={form}
                        sectionNo={sectionNo.amlScreening}
                      />
                    ) : (
                      <KycSectionAdverseMediaWithScreening
                        ref={adverseMediaScreeningRef}
                        onScreeningStateChange={setAdverseBusy}
                        readOnly={screeningReadOnly}
                        form={form}
                        onSetAt={setAt}
                        mode="staff"
                        sectionNo={sectionNo.amlScreening}
                        initiallyExpanded={bulkScreeningStarted}
                      />
                    )}
                  </section>
                </motion.div>
              ) : null}
            </AnimatePresence>

            {/* ============================ */}
            {/* Documents checklist (STAFF ONLY) */}
            {/* ============================ */}
            <section id="kyc-sec-docs">
              <KycSectionDocuments
                onDocumentIntakeStart={(files, context) => documentIntake.start(files, { ...context, source: 'checklist' })}
                intakeLoadingMessage={checklistIntakeBusy ? documentIntake.session.message : ''}
                onCancelDocumentIntake={documentIntake.cancelProcessing}
                onCompletionChange={setDocumentCompletion}
                form={form}
                readOnly={checklistReadOnly}
                onSetAt={setAt}
                sectionNo={visibleSectionNo.documents}
                clientId={id} // must be non-empty
                apiBaseUrl={SERVER_URL}
                headerStatusText={docUploadStatusText}
                headerEmailText={missingDocsEmailText}
                onHeaderStatusClick={docSubmissionId ? () => requestLeave(() => nav(`/docsReview/${docSubmissionId}`, { state: { from: "inbox" } })) : undefined}
                isAdmin={isAdmin}
                userRole={myRole}
                userOrgId={myOrgId}
                userTeamId={myTeamId}
                userUid={auth.currentUser?.uid || ""}
                relationshipRiskSettings={relationshipRiskSettings}
              />
            </section>

            {/* ============================ */}
            {/* FINAL OVERVIEW (STAFF ONLY) */}
            {/* ============================ */}
            <section id="kyc-sec-final">
              <KycSectionFinalOverview
                form={form}
                readOnly={finalOverviewReadOnly}
                onSetAt={setAt}
                sectionNo={visibleSectionNo.finalOverview}
                clientId={id} // must be non-empty
                relationshipRiskSettings={relationshipRiskSettings}
              />
            </section>

            {/* ====================================== */}
            {/* END - SECOND SECOND PART - COMPLIANCE ASSESSMENT */}
            {/* ====================================== */}
          </div>
          {/* ====================================== */}

        </form>
      </motion.div>

      <Toast ref={toastRef} baseZIndex={6000} />
      {leavePromptOpen && (
        <div
          className="kycUnsavedPromptBackdrop"
          role="presentation"
          onMouseDown={(e) => {
            if (e.target === e.currentTarget && !leavePromptSaving) closeLeavePrompt();
          }}
        >
          <div
            className="kycUnsavedPromptCard appConfirmCard appConfirm--warn"
            role="dialog"
            aria-modal="true"
            aria-labelledby="kycUnsavedPromptTitle"
          >
            <div className="appConfirmIcon">
              <i className="bi bi-exclamation-triangle" aria-hidden="true" />
            </div>
            <div id="kycUnsavedPromptTitle" className="appConfirmTitle">
              Unsaved KYC changes
            </div>
            <p className="appConfirmText">
              Save this KYC draft before leaving, or leave without saving your latest changes.
            </p>
            <div className="kycUnsavedPromptActions">
              <button
                type="button"
                className="appConfirmBtn appConfirmBtnPrimary kycUnsavedPromptSave"
                onClick={saveDraftAndLeave}
                disabled={leavePromptSaving}
              >
                {leavePromptSaving ? "Saving..." : "Save draft"}
              </button>
              <button
                type="button"
                className="appConfirmBtn appConfirmBtnSecondary"
                onClick={leaveWithoutSaving}
                disabled={leavePromptSaving}
              >
                Leave without saving
              </button>
              <button
                type="button"
                className="appConfirmBtn appConfirmBtnGhost"
                onClick={closeLeavePrompt}
                disabled={leavePromptSaving}
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
      {emailPromptOpen && (
        <div
          className="kycEmailPromptBackdrop"
          role="presentation"
          onMouseDown={(e) => {
            if (e.target === e.currentTarget) closeEmailPrompt(null);
          }}
        >
          <div
            className="kycEmailPromptCard"
            role="dialog"
            aria-modal="true"
            aria-labelledby="kycEmailPromptTitle"
          >
            <button
              type="button"
              className="kycEmailPromptClose"
              aria-label="Close"
              onClick={() => closeEmailPrompt(null)}
            >
              ×
            </button>
            <div className="kycEmailPromptIcon">
              <i className="bi bi-envelope" aria-hidden="true" />
            </div>
            <div id="kycEmailPromptTitle" className="kycEmailPromptTitle">
              Client email is missing
            </div>
            <p className="kycEmailPromptText">
              Enter the client email address to send the KYC form link.
            </p>
            <input
              ref={emailPromptInputRef}
              type="email"
              className={`kycEmailPromptInput ${
                emailPromptError ? "isInvalid" : ""
              }`}
              value={emailPromptValue}
              placeholder="client@example.com"
              onChange={(e) => {
                setEmailPromptValue(e.target.value);
                if (emailPromptError) setEmailPromptError("");
              }}
              onKeyDown={(e) => {
                if (e.key === "Enter") submitEmailPrompt();
                if (e.key === "Escape") closeEmailPrompt(null);
              }}
            />
            {emailPromptError ? (
              <div className="kycEmailPromptError">{emailPromptError}</div>
            ) : null}
            <div className="kycEmailPromptActions">
              <button
                type="button"
                className="kycEmailPromptCancel"
                onClick={() => closeEmailPrompt(null)}
              >
                Cancel
              </button>
              <button
                type="button"
                className="kycEmailPromptSubmit"
                onClick={submitEmailPrompt}
              >
                Send link
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
    </OwnershipLabelsProvider>
  );
}
